(() => {
  if (customElements.get('venora-related-reviews')) return;
  const requests = new Map();
  const pageSize = 24;

  class VenoraRelatedReviews extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.list = this.querySelector('[data-review-list]');
      this.status = this.querySelector('[data-review-status]');
      this.error = this.querySelector('[data-review-error]');
      this.loadButton = this.querySelector('[data-review-load]');
      this.previous = this.querySelector('[data-review-previous]');
      this.next = this.querySelector('[data-review-next]');
      this.pagination = this.querySelector('[data-review-pagination]');
      this.motionButton = this.querySelector('[data-review-motion]');
      if (!this.list || !this.status || !this.error || !this.loadButton ||
          !this.previous || !this.next || !this.pagination) return;
      this.loadButton.addEventListener('click', () => this.load(), options);
      this.previous.addEventListener('click', () => this.changePage(-1), options);
      this.next.addEventListener('click', () => this.changePage(1), options);
      this.setupMotion(options);
      if (this.data) {
        this.render();
        return;
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.observer?.disconnect();
            this.load();
          }
        }, { rootMargin: '300px 0px' });
        this.observer.observe(this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.stopMotion();
      this.loading = false;
    }

    setupMotion(options) {
      this.motionVisible = false;
      this.motionHovered = false;
      this.motionFocused = false;
      this.motionDirection = this.motionDirection || 1;
      this.motionPaused = this.motionPaused || false;
      this.motionSupported = 'IntersectionObserver' in window;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const update = () => this.updateMotion();
      this.reducedMotion.addEventListener('change', update, options);
      this.ownerDocument.addEventListener('visibilitychange', update, options);
      window.addEventListener('resize', update, options);
      this.list.addEventListener('pointerenter', event => {
        if (event.pointerType === 'touch') return;
        this.motionHovered = true;
        this.updateMotion();
      }, options);
      this.list.addEventListener('pointerleave', () => {
        this.motionHovered = false;
        this.updateMotion();
      }, options);
      this.list.addEventListener('focusin', () => {
        this.motionFocused = true;
        this.updateMotion();
      }, options);
      this.list.addEventListener('focusout', event => {
        this.motionFocused = this.list.contains(event.relatedTarget);
        this.updateMotion();
      }, options);
      const pause = () => {
        this.motionPaused = true;
        this.updateMotion();
      };
      this.list.addEventListener('pointerdown', pause, { ...options, passive: true });
      this.list.addEventListener('wheel', event => {
        if (event.deltaX || (event.shiftKey && event.deltaY)) pause();
      }, { ...options, passive: true });
      this.list.addEventListener('keydown', event => {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) pause();
      }, options);
      this.motionButton?.addEventListener('click', () => {
        this.motionPaused = !this.motionPaused;
        this.updateMotion();
      }, options);
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(entries => {
          this.motionVisible = entries.some(entry => entry.isIntersecting);
          this.updateMotion();
        });
        this.visibilityObserver.observe(this.list);
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(update);
        this.resizeObserver.observe(this.list);
      }
      this.updateMotion();
    }

    updateMotion() {
      if (!this.list) return;
      const overflow = this.list.scrollWidth > this.list.clientWidth + 1;
      const reduced = this.reducedMotion?.matches;
      this.list.classList.toggle('has-review-motion', overflow && this.motionSupported && !reduced);
      if (this.motionButton) {
        this.motionButton.hidden = !this.data || !overflow || reduced || !this.motionSupported;
        this.motionButton.textContent = this.motionPaused ? 'Play' : 'Pause';
        this.motionButton.setAttribute('aria-label', this.motionPaused ? 'Play review animation' : 'Pause review animation');
      }
      const running = this.isConnected && !this.controller?.signal.aborted &&
        this.data && overflow && this.motionVisible && !this.ownerDocument.hidden &&
        !reduced && !this.motionPaused && !this.motionHovered && !this.motionFocused;
      if (!running) {
        this.stopMotion();
        return;
      }
      if (this.motionFrame != null) return;
      this.motionPosition = this.list.scrollLeft;
      this.motionTime = null;
      this.list.classList.add('is-moving');
      const step = time => {
        this.motionFrame = null;
        const end = this.list.scrollWidth - this.list.clientWidth;
        if (!this.isConnected || this.controller.signal.aborted || end <= 1) {
          this.stopMotion();
          return;
        }
        const elapsed = this.motionTime == null ? 0 : Math.min(time - this.motionTime, 64);
        this.motionTime = time;
        this.motionPosition += this.motionDirection * 22 * elapsed / 1000;
        if (this.motionPosition >= end) {
          this.motionPosition = end;
          this.motionDirection = -1;
        } else if (this.motionPosition <= 0) {
          this.motionPosition = 0;
          this.motionDirection = 1;
        }
        this.list.scrollLeft = this.motionPosition;
        this.motionFrame = requestAnimationFrame(step);
      };
      this.motionFrame = requestAnimationFrame(step);
    }

    stopMotion() {
      if (this.motionFrame != null) cancelAnimationFrame(this.motionFrame);
      this.motionFrame = null;
      this.motionTime = null;
      this.list?.classList.remove('is-moving');
    }

    async load() {
      if (this.loading || this.data || !this.dataset.source) return;
      const controller = this.controller;
      this.loading = true;
      this.loadButton.disabled = true;
      this.error.hidden = true;
      this.status.textContent = 'Loading bag reviews…';
      this.list.setAttribute('aria-busy', 'true');
      try {
        if (!requests.has(this.dataset.source)) {
          const request = fetch(this.dataset.source, { credentials: 'omit' }).then(response => {
            if (!response.ok) throw new Error('Reviews unavailable');
            return response.json();
          }).catch(error => {
            requests.delete(this.dataset.source);
            throw error;
          });
          requests.set(this.dataset.source, request);
        }
        const data = await requests.get(this.dataset.source);
        if (!this.isConnected || controller.signal.aborted) return;
        this.setData(data);
        this.render();
      } catch {
        if (!this.isConnected || controller.signal.aborted) return;
        this.status.textContent = '';
        this.error.textContent = 'Reviews could not load. Please try again.';
        this.error.hidden = false;
        this.loadButton.textContent = 'Try again';
        this.loadButton.hidden = false;
      } finally {
        if (this.controller === controller) {
          this.loading = false;
          this.loadButton.disabled = false;
          this.list.setAttribute('aria-busy', 'false');
        }
      }
    }

    setData(data) {
      if (data?.schemaVersion !== 1 || !Array.isArray(data.products) || !Array.isArray(data.reviews)) {
        throw new Error('Invalid reviews');
      }
      const products = new Map();
      for (const product of data.products) {
        if (!/^[a-z0-9-]+$/.test(product.handle) || typeof product.title !== 'string' || !product.title || products.has(product.handle) ||
            (product.id != null && !/^\d+$/.test(String(product.id)))) {
          throw new Error('Invalid review source');
        }
        products.set(product.handle, product);
      }
      const ids = new Set();
      for (const review of data.reviews) {
        if (!products.has(review.productHandle) || typeof review.id !== 'string' || !review.id || ids.has(review.id) ||
            !Number.isInteger(review.rating) || review.rating < 1 || review.rating > 5 ||
            ['heading', 'body', 'author', 'date', 'reply_author', 'reply'].some(key => typeof review[key] !== 'string') ||
            (review.verification != null && typeof review.verification !== 'string') ||
            (review.helpfulCount != null && (!Number.isSafeInteger(review.helpfulCount) || review.helpfulCount < 0)) ||
            (review.commentId != null && (typeof review.commentId !== 'string' || !/^[A-Za-z0-9_-]+$/.test(review.commentId)))) {
          throw new Error('Invalid review');
        }
        ids.add(review.id);
      }
      this.products = products;
      this.data = data;
      this.page = 0;
      this.loadButton.hidden = true;
    }

    render() {
      if (!this.data) return;
      const reviews = this.data.reviews;
      const pages = Math.max(1, Math.ceil(reviews.length / pageSize));
      this.page = Math.max(0, Math.min(this.page || 0, pages - 1));
      const start = this.page * pageSize;
      const fragment = this.ownerDocument.createDocumentFragment();
      for (const review of reviews.slice(start, start + pageSize)) fragment.append(this.card(review));
      this.list.replaceChildren(fragment);
      this.previous.disabled = this.page === 0;
      this.next.disabled = this.page >= pages - 1;
      this.pagination.hidden = reviews.length <= pageSize;
      this.status.textContent = reviews.length
        ? `Bag reviews ${start + 1} through ${Math.min(start + pageSize, reviews.length)} of ${reviews.length}.`
        : 'No bag reviews are available.';
      this.list.scrollLeft = 0;
      this.list.scrollTop = 0;
      this.stopMotion();
      this.motionDirection = 1;
      this.updateMotion();
    }

    changePage(direction) {
      const pages = Math.ceil(this.data.reviews.length / pageSize);
      const target = this.page + direction;
      if (target < 0 || target >= pages) return;
      this.page = target;
      this.render();
      this.list.focus({ preventScroll: true });
      this.list.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    }

    element(tag, text, className) {
      const element = this.ownerDocument.createElement(tag);
      if (text != null) element.textContent = text;
      if (className) element.className = className;
      return element;
    }

    photo(review, product) {
      const image = review.image;
      if (!image || !/^estoria-ref-[a-z0-9-]+\.webp$/.test(image.filename) ||
          !Number.isInteger(image.width) || image.width <= 0 ||
          !Number.isInteger(image.height) || image.height <= 0) return null;
      let source;
      try {
        const root = new URL(this.dataset.imageRoot, this.ownerDocument.baseURI);
        if (root.protocol !== 'https:' || !root.pathname.endsWith('/assets/') ||
            root.search || root.hash) return null;
        source = new URL(image.filename, root);
        if (!source.href.startsWith(root.href)) return null;
      } catch {
        return null;
      }
      const figure = this.element('figure', null, 'venora-related-reviews__media');
      const photo = this.element('img');
      photo.width = image.width;
      photo.height = image.height;
      photo.loading = 'lazy';
      photo.decoding = 'async';
      photo.alt = `${product.title} bag photo`;
      photo.addEventListener('error', () => { figure.hidden = true; }, { once: true });
      photo.src = source.href;
      figure.append(photo);
      return figure;
    }

    card(review) {
      const product = this.products.get(review.productHandle);
      const article = this.element('article', null, 'venora-related-reviews__card');
      article.dataset.reviewId = review.id;
      article.setAttribute('role', 'listitem');
      const photo = this.photo(review, product);
      if (photo) article.append(photo);
      const content = this.element('div', null, 'venora-related-reviews__content');
      const source = this.element('a', product.title, 'venora-related-reviews__source');
      const root = this.dataset.productRoot || '/products/';
      source.href = `${root}${product.handle}#EstoriaComments`;
      const stars = this.element('div', null, 'venora-related-reviews__stars');
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      for (let index = 1; index <= 5; index++) {
        const star = this.element('span', index <= review.rating ? '★' : '☆');
        star.setAttribute('aria-hidden', 'true');
        stars.append(star);
      }
      content.append(stars);
      const copy = this.element('div', null, 'venora-related-reviews__copy');
      copy.tabIndex = 0;
      copy.setAttribute('role', 'group');
      copy.setAttribute('aria-label', `Review of ${product.title} by ${review.author}`);
      if (review.heading) copy.append(this.element('h3', review.heading));
      const quote = this.element('blockquote');
      quote.append(this.element('p', review.body));
      copy.append(quote);
      const attribution = this.element('div', null, 'venora-related-reviews__attribution');
      attribution.append(this.element('cite', review.author));
      if (review.date) attribution.append(this.element('span', review.date));
      if (review.verification) attribution.append(this.element('span', review.verification));
      if (review.reply) {
        const reply = this.element('div', null, 'venora-related-reviews__reply');
        if (review.reply_author) reply.append(this.element('p', review.reply_author, 'venora-related-reviews__reply-author'));
        reply.append(this.element('p', review.reply));
        copy.append(reply);
      }
      content.append(copy, attribution, source);
      const helpful = this.helpful(review, product);
      if (helpful) content.append(helpful);
      article.append(content);
      return article;
    }

    helpful(review, product) {
      if (!product.id || !review.commentId || !Number.isSafeInteger(review.helpfulCount) || review.helpfulCount < 0) return null;
      const action = this.element('estoria-review-actions', null, 'venora-related-reviews__helpful');
      action.dataset.productId = String(product.id);
      action.dataset.commentId = review.commentId;
      action.dataset.baseCount = String(review.helpfulCount);
      for (const key of ['savedMessage', 'unavailableMessage', 'addedMessage', 'removedMessage']) {
        action.dataset[key] = this.dataset[key] || '';
      }
      const noteId = `VenoraRelatedHelpfulNote-${review.id.replace(/[^A-Za-z0-9_-]/g, '-')}`;
      const button = this.element('button');
      button.type = 'button';
      button.disabled = true;
      button.setAttribute('data-helpful-toggle', '');
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-describedby', noteId);
      button.title = action.dataset.savedMessage;
      const svg = this.ownerDocument.createElementNS('http://www.w3.org/2000/svg', 'svg');
      for (const [name, value] of Object.entries({
        width: '20', height: '20', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
        'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true', focusable: 'false'
      })) svg.setAttribute(name, value);
      for (const d of [
        'M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z',
        'M7 10v12'
      ]) {
        const shape = this.ownerDocument.createElementNS('http://www.w3.org/2000/svg', 'path');
        shape.setAttribute('d', d);
        svg.append(shape);
      }
      const count = this.element('span', String(review.helpfulCount));
      count.setAttribute('data-helpful-count', '');
      button.append(svg, this.element('span', this.dataset.helpfulLabel || 'Helpful'), count);
      const note = this.element('span', action.dataset.savedMessage);
      note.id = noteId;
      note.setAttribute('data-helpful-note', '');
      note.setAttribute('role', 'tooltip');
      const status = this.element('span', null, 'venora-related-reviews__sr-only');
      status.setAttribute('data-helpful-status', '');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      action.append(button, note, status);
      return action;
    }
  }
  customElements.define('venora-related-reviews', VenoraRelatedReviews);
})();
